// @ts-strict-ignore
import * as React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectShouldShowAutomationsTimelineEventsOnly,
    selectTimelineEventsFiltersMap,
} from '../../selectors/selectors';
import { useGetOrgTimelineEventsQuery, useLazyGetOrgTimelineEventsQuery } from '../../middlewares/paginationApi';
import { globalMessage } from '@/utils/globalMessage';
import { filterTimelineEvent } from '../../types/timelineEventsFilters';
import { Timeline } from './TimelineEventsContainer';
import { ReleaseTimelineSectionContext } from './EventsTimeline';
import Spin from 'antd/lib/spin';
import { useCallback, useEffect, useState } from 'react';
import type { PaginationInfo } from '@/actions/pagination';
import type { TimelineEvent } from '../../types';
import { uniq, compact } from 'lodash';
import dayjs from 'dayjs';

interface OrgTimelineEventsProps {
    orgId: string;
}

const OrgTimelineEvents: React.FC<OrgTimelineEventsProps> = ({ orgId }) => {
    const [allEvents, setAllEvents] = useState<TimelineEvent[]>([]);
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>(null);
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(true);

    const initialQuery = useGetOrgTimelineEventsQuery({ orgId });
    const [fetchMore, lazyResult] = useLazyGetOrgTimelineEventsQuery();

    const { data: initialData, isLoading } = initialQuery;
    const { data: moreData, isLoading: isLazyLoading } = lazyResult;

    useEffect(() => {
        if (initialData?.data) {
            setAllEvents(initialData.data.events);
            setPaginationInfo(initialData.data.paginationInfo);
        }
        setSuccess(!initialData?.errorMessage?.length);
    }, [initialData]);

    useEffect(() => {
        if (moreData?.data) {
            const newEvents = moreData.data.events;
            const updatedEvents = sortCombinedEvents(allEvents, newEvents);
            setAllEvents(updatedEvents);
            setPaginationInfo(moreData.data.paginationInfo);
            setIsLoadingMore(false);
        }
        setSuccess(!moreData?.errorMessage?.length);
    }, [moreData]);

    const loadMore = useCallback(() => {
        if (!paginationInfo || isLoadingMore || !paginationInfo.hasMore) return;
        setIsLoadingMore(true);
        fetchMore({ orgId, nextCursor: paginationInfo.nextCursor });
    }, [paginationInfo, isLoadingMore, fetchMore, orgId]);

    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const shouldShowAutomationsOnly = useSelector((state: RootState) =>
        selectShouldShowAutomationsTimelineEventsOnly(state),
    );

    useEffect(() => {
        if (!success) {
            globalMessage.error('An error occurred when fetching timeline events.');
        }
    }, [success]);

    const filteredEvents = allEvents.filter((event) =>
        filterTimelineEvent(event, currentFiltersMap, shouldShowAutomationsOnly),
    );

    const availableVersions = uniq(compact(allEvents.map((event) => event.releaseData?.displayableVersion)));

    if (isLoading) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100vh',
                    flexGrow: 1,
                }}
            >
                <Spin />
            </div>
        );
    }

    return (
        <Timeline
            releaseId={null}
            appId={null}
            orgId={orgId}
            events={filteredEvents}
            isDrawerOpen={false}
            loadMore={loadMore}
            isLoading={isLoadingMore || isLazyLoading}
            hasMore={paginationInfo?.hasMore}
            context={ReleaseTimelineSectionContext.OrgTimeline}
            availableVersions={availableVersions}
        />
    );
};

const sortCombinedEvents = (allEvents: TimelineEvent[], newEvents: TimelineEvent[]) => {
    const updatedEvents = [...allEvents, ...newEvents];

    if (newEvents.length && allEvents.length) {
        const newestDate = dayjs(newEvents[0].triggeredAt);
        const oldestDate = dayjs(allEvents[allEvents.length - 1].triggeredAt);

        if (newestDate.isAfter(oldestDate)) {
            updatedEvents.sort((a, b) => dayjs(b.triggeredAt).diff(a.triggeredAt));
        }
    }

    return updatedEvents;
};

export default OrgTimelineEvents;
